/**
 * ProtectedRoute — redirects to /login if the user isn't authenticated.
 *
 * Shows a minimal loading spinner while the initial session check runs.
 * Passes the target path via location.state so LoginPage can redirect back.
 *
 * Preview bypass: `?preview=1` in the URL renders children without auth.
 * Used by the Hub iframe previews and local UI work when the backend /
 * cognito flow isn't available.
 */

import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import type { ReactNode } from "react";

const UI_ONLY_MODE =
  String(import.meta.env.VITE_UI_ONLY_MODE ?? "").toLowerCase() === "1" ||
  String(import.meta.env.VITE_UI_ONLY_MODE ?? "").toLowerCase() === "true";

const PREVIEW_FLAG = "foreman_preview_mode";

function isPreviewMode(): boolean {
  if (typeof window === "undefined") return false;
  const fromQuery =
    new URLSearchParams(window.location.search).get("preview") === "1";
  if (fromQuery) {
    try {
      sessionStorage.setItem(PREVIEW_FLAG, "1");
    } catch {}
    return true;
  }
  try {
    return sessionStorage.getItem(PREVIEW_FLAG) === "1";
  } catch {
    return false;
  }
}

export default function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, loading, needsOnboarding } = useAuth();
  const location = useLocation();

  if (UI_ONLY_MODE || isPreviewMode()) {
    return <>{children}</>;
  }

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          background: "#f3f2ed",
          fontFamily:
            "'Clash Grotesk', -apple-system, BlinkMacSystemFont, sans-serif",
          color: "#5a5e54",
          fontSize: 14,
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: 32,
              height: 32,
              border: "3px solid #d2d1cb",
              borderTopColor: "#315c2b",
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
              margin: "0 auto 12px",
            }}
          />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          Checking session…
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (needsOnboarding && location.pathname !== "/onboard") {
    return <Navigate to="/onboard" replace />;
  }

  return <>{children}</>;
}
